import React from 'react';
import { LockKeyhole } from 'lucide-react';
import { Button } from '../../ui/button';
import type { PathToGoScreeningDecision } from '../../../types/pathToGoDecision';

export interface ScreeningDecisionDraft {
  decision: PathToGoScreeningDecision;
  rationale: string;
  decisionOwner: string;
  /** Reused on an unchanged retry; a changed draft starts a new submission. */
  submissionId: string;
}

const LABEL: Record<PathToGoScreeningDecision, string> = {
  PROCEED: 'Proceed', PROCEED_WITH_CONCERNS: 'Proceed with concerns', DECLINE: 'Decline', DEFER: 'Defer',
};

export function ScreeningDecisionRecordForm({ onRecordDecision, positiveDecisionGateSatisfied, gateReason,
  decisionOwnerDefault = '', decisionReadOnly = false }: {
  onRecordDecision: (draft: ScreeningDecisionDraft) => void | Promise<void>;
  positiveDecisionGateSatisfied: boolean;
  gateReason?: string | null;
  decisionOwnerDefault?: string;
  decisionReadOnly?: boolean;
}) {
  const [decision, setDecision] = React.useState<PathToGoScreeningDecision>('DEFER');
  const [rationale, setRationale] = React.useState('');
  const [owner, setOwner] = React.useState(decisionOwnerDefault);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const submission = React.useRef<{ fingerprint: string; id: string } | null>(null);
  const positive = decision === 'PROCEED' || decision === 'PROCEED_WITH_CONCERNS';
  return (
    <form className="space-y-3 border-t border-border-subtle pt-4" data-testid="screening-decision-record-form" onSubmit={async event => {
      event.preventDefault();
      if (decisionReadOnly || submitting) return;
      if (positive && !positiveDecisionGateSatisfied) { setError(gateReason ?? 'A current eligible saved screening is required before proceeding.'); return; }
      if (!rationale.trim() || !owner.trim()) { setError('Add a rationale and decision owner.'); return; }
      const fingerprint = JSON.stringify([decision, rationale.trim(), owner.trim()]);
      const submissionId = submission.current?.fingerprint === fingerprint ? submission.current.id : crypto.randomUUID();
      submission.current = { fingerprint, id: submissionId };
      setSubmitting(true); setError(null);
      try {
        await onRecordDecision({ decision, rationale: rationale.trim(), decisionOwner: owner.trim(), submissionId });
        setRationale(''); submission.current = null;
      } catch (failure) { setError(failure instanceof Error ? failure.message : 'The screening decision could not be recorded.'); }
      finally { setSubmitting(false); }
    }}>
      <div>
        <p className="type-meta font-bold text-text-secondary">Record the screening decision</p>
        <p className="mt-1 text-xs text-text-muted">This records whether to proceed to validation and freezes the saved screening source and recommendations. It does not record a final GO or adopt an Intent choice.</p>
      </div>
      <fieldset className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <legend className="sr-only">Screening decision</legend>
        {(['PROCEED', 'PROCEED_WITH_CONCERNS', 'DECLINE', 'DEFER'] as const).map(option => {
          const disabled = decisionReadOnly || submitting || ((option === 'PROCEED' || option === 'PROCEED_WITH_CONCERNS') && !positiveDecisionGateSatisfied);
          return <label key={option} className={`relative flex min-h-11 items-center justify-center gap-1.5 rounded-lg border px-2 type-meta font-bold focus-within:ring-2 focus-within:ring-primary ${decision === option ? 'border-primary bg-primary/5 text-primary' : disabled ? 'cursor-not-allowed border-border-subtle text-text-muted opacity-50' : 'cursor-pointer border-border text-text-secondary hover:text-text-primary'}`}
            data-testid={`screening-decision-choice-${option}`} data-disabled={disabled ? 'true' : 'false'}>
            <input type="radio" name="screening-decision" value={option} checked={decision === option} disabled={disabled} onChange={() => setDecision(option)} className="sr-only" />
            {disabled && <LockKeyhole className="h-3 w-3" aria-hidden="true" />}{LABEL[option]}
          </label>;
        })}
      </fieldset>
      {!positiveDecisionGateSatisfied && <p className="rounded-lg border border-warning/40 bg-warning/5 p-2.5 text-xs text-warning" data-testid="screening-positive-gate">{gateReason ?? 'A current eligible saved screening is required for Proceed or Proceed with concerns.'} Decline and Defer remain available.</p>}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,0.45fr)]">
        <label className="block"><span className="type-meta font-bold text-text-secondary">Rationale</span>
          <textarea value={rationale} onChange={event => setRationale(event.target.value)} rows={3} disabled={decisionReadOnly || submitting} data-testid="screening-decision-rationale"
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50" />
        </label>
        <label className="block"><span className="type-meta font-bold text-text-secondary">Decision owner</span>
          <input value={owner} onChange={event => setOwner(event.target.value)} disabled={decisionReadOnly || submitting} data-testid="screening-decision-owner"
            className="mt-1 min-h-11 w-full rounded-md border border-border bg-background px-3 text-sm text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50" />
        </label>
      </div>
      {error && <p role="alert" className="text-xs text-danger" data-testid="screening-decision-record-error">{error}</p>}
      <Button type="submit" variant="mission-primary" className="min-h-11" disabled={decisionReadOnly || submitting} data-testid="screening-decision-submit">{submitting ? 'Recording…' : 'Record screening decision'}</Button>
    </form>
  );
}
